/**
 * utilities/activeCountry.js
 * ─────────────────────────────────────────────────────────────────────────────
 * A tiny, framework-free store for the dashboard's ACTIVE country (the ISO-2
 * market whose data the operator is currently viewing). It exists so the axios
 * request interceptor — which cannot call a React hook — can read the current
 * active country SYNCHRONOUSLY and stamp `x-country` on every request.
 *
 * Source of truth ordering for a read (getActiveCountry):
 *   1. an in-memory value set this session (fastest, avoids re-parsing storage)
 *   2. the persisted `localStorage.activeCountry` (a super-admin's last pick)
 *   3. the logged-in user's HOME country (`profile.result.country`)
 *   4. DEFAULT_COUNTRY ('UG') — legacy sessions with no country stamped
 *
 * The value is persisted under its OWN key (`activeCountry`) — we NEVER mutate
 * `localStorage.profile` (that is AES-encrypted auth state). The CountryProvider
 * writes through setActiveCountry so this store and React state stay aligned,
 * and a `storage` event keeps other tabs in sync.
 *
 * SECURITY NOTE: this is ADVISORY only. The header is a convenience for the
 * server; the backend (`enforceStaffCountry`) is the boundary and will ignore /
 * 403 a country a non-super identity is not permitted to see.
 */

import { DEFAULT_COUNTRY, normalizeCountry } from "../config/markets.js";
import { getUserFromLocalStorage } from "./user.js";

export const ACTIVE_COUNTRY_KEY = "activeCountry";

// In-memory cache of the last set/read value. `null` = not yet resolved this
// session, so getActiveCountry() will (re)derive it from storage/profile.
let current = null;

/** Best-effort read of the logged-in user's HOME country from the profile. */
function homeCountryFromProfile() {
  try {
    const profile = getUserFromLocalStorage();
    return normalizeCountry(profile?.result?.country);
  } catch (err) {
    return null;
  }
}

/** Best-effort read of the persisted active-country selection. */
function persistedActiveCountry() {
  try {
    return normalizeCountry(localStorage.getItem(ACTIVE_COUNTRY_KEY));
  } catch (err) {
    return null;
  }
}

/**
 * Synchronously resolve the active country. Never throws, never returns null —
 * always a valid ISO-2 (falls back to DEFAULT_COUNTRY).
 * @returns {string}
 */
export function getActiveCountry() {
  if (current) return current;
  const resolved =
    persistedActiveCountry() || homeCountryFromProfile() || DEFAULT_COUNTRY;
  current = resolved;
  return resolved;
}

/**
 * Set (and persist) the active country. Normalizes the input; an unsupported
 * value is ignored (keeps the previous/derived value) so a bad call can never
 * blank the header. Returns the effective country after the set.
 * @param {*} iso2
 * @returns {string}
 */
export function setActiveCountry(iso2) {
  const next = normalizeCountry(iso2);
  if (!next) return getActiveCountry();
  current = next;
  try {
    localStorage.setItem(ACTIVE_COUNTRY_KEY, next);
  } catch (err) {
    // Storage may be unavailable (private mode / quota); the in-memory value
    // still drives this tab's requests.
  }
  return next;
}

/**
 * Clear the in-memory cache (e.g. on logout / user switch) so the next read
 * re-derives from the freshly-written profile. Does not touch persisted storage.
 */
export function resetActiveCountry() {
  current = null;
}
